// Which lots are NEW since the board last refreshed — so they can flash.
//
// Owner, 2026-09-14: "when new lots come in can it do a big flash so it looks
// alive?" The homepage board had its own copy of this bookkeeping; the barn and
// state tickers and the Steer Tracker had none, so a new pen slid in silently.
//
// Rules, same as the homepage's original:
//   • the FIRST board ever shown flashes nothing (everything would be "new");
//   • a SCOPE change (another tab, barn or state) flashes nothing — the whole
//     board was swapped, and flashing all of it would mean nothing;
//   • after that, any lot id not on the previous board is new, and stays new for
//     FLASH_MS, long enough for the animation in tickerHome.css to play once.

import { useEffect, useRef, useState } from "react";

import type { TickerBand } from "@/lib/rbCattleTicker";

/** Matches the tx-newlot animation in tickerHome.css. */
export const FLASH_MS = 3200;

export function useFreshLots(bands: TickerBand[] | null | undefined, scopeKey: string): Set<string> {
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const seen = useRef<{ scope: string; ids: Set<string> } | null>(null);

  useEffect(() => {
    if (!bands) return;
    const ids = new Set<string>();
    for (const b of bands) for (const l of b.lots) ids.add(l.id);

    const prev = seen.current;
    seen.current = { scope: scopeKey, ids };
    if (!prev || prev.scope !== scopeKey) return;

    const added = new Set<string>();
    for (const id of ids) if (!prev.ids.has(id)) added.add(id);
    if (added.size) setFresh(added);
  }, [bands, scopeKey]);

  useEffect(() => {
    if (!fresh.size) return;
    const t = setTimeout(() => setFresh(new Set()), FLASH_MS);
    return () => clearTimeout(t);
  }, [fresh]);

  return fresh;
}
